import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { debounce } from 'underscore';

import { chatPublicDomain } from '../../util/config';
import { generateUUID, isUsedOnMobile, joinClassNames } from '../../util/util';
import { useChatLogin } from '../../contexts/ChatLoginContext';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { usePlayer } from '../../contexts/PlayerContext';
import { usePresenter } from '../../contexts/PresenterContext';
import { PresenterHelper } from '../Presenter/presenter-helper';

import Poll from '../Poll/Poll';
import ChatUpdated from '../ChatUpdated/ChatUpdated';
import QuestionPanel from '../Q&A/QuestionPanel';

import ChatLoginOverlay from './ChatLoginOverlay';
import { ChatPinnedMessage } from './PinnedMessage.styled';
import { ChatBottom } from './ChatBottom.styled';
import { ChatTop } from './ChatTop.styled';
import { ChatOverlayContainer, ChatOverlayWrapper, ChatWrapper } from './Chat.styled';
import { ChatButtonsWrapper } from './ChatButtons.styled';
import { NOTIFICATION_MESSAGES } from './helpers';
import { NotificationUtils } from './NotificationUtils';

import './ChatOverlay.scss';

interface Message {
    id: string;
    sender: {
        userId: string;
        name: string;
        email: string;
        admin: boolean;
    };
    message: {
        text?: string;
        pinned?: boolean;
        emoji?: {
            unicode: string;
            level: number;
        };
    };
    recipient?: {
        replyMessageId?: string;
    };
    created?: string;
    timestamp?: string;
}

interface Poll {
    id: string;
    published?: boolean;
    closed?: boolean;
    result?: any;
}

interface QnA {
    id: string;
    published?: boolean;
}

interface Question {
    id: string;
    question?: string;
    text?: string;
    approved?: boolean;
    discarded?: boolean;
    votes?: number;
    created?: string;
    timestamp?: string;
    sender?: any;
    recipient?: {
        replyQuestionId?: string;
    };
    reply?: any;
}

interface ChatOverlayProps {
    settings?: any;
    playerHover?: boolean;
    showChatWindow?: boolean;
    hideRightControls?: boolean;
    onTouchStart?: (_e: React.TouchEvent) => void;
    onTouchMove?: (_e: React.TouchEvent) => void;
    getRestrictToken: (_mediaId?: string) => string;
}

const ChatOverlay: React.FC<ChatOverlayProps> = ({ settings, playerHover, onTouchStart, onTouchMove, getRestrictToken }) => {
    const { t } = useTranslation();

    // Chat Login Context
    const { username, email, userId, setShowLoginForm, setUsername: setChatUsername, setEmail: setChatEmail, setUserId: setChatUserId, showMobileChat } = useChatLogin();

    // Presenter Context
    const { presentationPublishedSlides } = usePresenter();

    // Configuration Context
    const {
        mediaName,
        accountId,
        currentMediaId: mediaId,
        playlistId,
        viewerEngagementModuleActivated,
        isChatActivated,
        isMediaLive,
        reactionUrl,
        isPollExpanded,
        mediasData,
        showCountdownModule,
        isCloseIncomingMessage,
        configParams,
        setReactionUrl,
        setPollExpanded,
        setViewerEngagementModuleActivated,
        setChatActivated,
        setMediasData,
        setCloseIncomingMessage,
        addTranscription,
	    setLiveTranscriptLanguage,
	    setEnabledLiveTranscript,
        setHasPublishedEngagement,
    } = useConfiguration();

    // Player Context
    const {
        duration,
        currentPosition,
        showChatWindow,
        // polls,
        // qnas,
        // questions,
        playerReady,
        showShoppingCart,
        reactionId,
        isPlaying,
        setShowBackgroundElements,
        setReactionId,
        setPollsData,
        setQnAData,
        setQuestionsData,
        setSeenEngagementCounts,
        setShowChatWindow,
    } = usePlayer();

    // Local state
    // const [showPinnedMessage, setShowPinnedMessage] = useState(false);
    const [inputChatString, setInputChatString] = useState('');
    const [showPicker, setShowPicker] = useState(false);
    const [messagesArray, setMessagesArray] = useState<Message[]>([]);
    const [pinnedMessagesArray, setPinnedMessagesArray] = useState<Message[]>([]);
    const [getChatUrl, setGetChatUrl] = useState('');
    const [chatVisible, setChatVisible] = useState(true);
    const [basketItemCount, setBasketItemCount] = useState(0);
    const [_pollArray, setPollArray] = useState<Poll[]>([]);
    const [publishedPoll, setPublishedPoll] = useState<Poll | undefined>(undefined);
    const [pollResult, setPollResult] = useState<any | undefined>(undefined);
    const [disabledChatInput, setDisabledChatInput] = useState(false);
    const [publishedQnA, setPublishedQnA] = useState<QnA | undefined>(undefined);
    const [approvedQuestions, setApprovedQuestions] = useState<Question[]>([]);
    const [_allQuestions, setAllQuestions] = useState<Question[]>([]);

    useEffect(() => {
        setHasPublishedEngagement(!!publishedQnA || !!publishedPoll || !!pollResult || approvedQuestions.length > 0);
    }, [publishedQnA, publishedPoll, pollResult, approvedQuestions]); // eslint-disable-line react-hooks/exhaustive-deps

    // Refs
    const messageContainerRef = useRef<HTMLDivElement>(null);
    const justSendMessage = useRef(false);
    const justSendQuestion = useRef(false);
    const webSocketConnection = useRef<WebSocket | undefined>(undefined);
    const chatWrapperBottom = useRef(0);

    const container = !configParams?.qplayerPreview ? configParams?.container ?? document : document;
    const isIgnoreChatCookies = useCallback(() => {
        if (configParams.ignoreChatCookies) {
            return configParams.ignoreChatCookies === 'true';
        }

        return settings?.additionalSettings?.noChatCookies === true;
    }, [configParams.ignoreChatCookies, settings?.additionalSettings?.noChatCookies]);

    const sendQuestion = useCallback(
        debounce((event: any, questionBody: any) => {
            justSendQuestion.current = true;

            if (!publishedQnA) {
                return;
            }

            event?.preventDefault?.();
            const questionUrl = `${chatPublicDomain}${accountId}/medias/${mediaId}/qnas/${publishedQnA.id}/questions`;
            fetch(questionUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(questionBody),
            });

            if ((window as any).mobileCheck && (window as any).mobileCheck()) {
                (document.activeElement as HTMLElement)?.blur();
            }
        }, 2000),
        [publishedQnA, accountId, mediaId]
    );

    const upvoteQuestion = useCallback(
        debounce((event: any, questionId: string, qnaId: string) => {
            justSendQuestion.current = true;

            event?.preventDefault?.();
            const upvoteQuestionUrl = `${chatPublicDomain}${accountId}/medias/${mediaId}/qnas/${qnaId}/questions/${questionId}/upvote`;
            fetch(upvoteQuestionUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            });

            if (!isIgnoreChatCookies()) {
                const upvotedQuestions = Cookies.get(`chat_upvoted_${accountId}`);
                const newUpvotedQuestion = `${upvotedQuestions ?? ''},${questionId}`;
                Cookies.set(`chat_upvoted_${accountId}`, newUpvotedQuestion);
            }

            if ((window as any).mobileCheck && (window as any).mobileCheck()) {
                (document.activeElement as HTMLElement)?.blur();
            }
        }, 2000),
        [accountId, mediaId, isIgnoreChatCookies]
    );

    const sendMessage = useCallback(
        debounce((event: any, messageBody: any) => {
            justSendMessage.current = true;
            event?.preventDefault?.();

            fetch(getChatUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(messageBody),
            });

            setDisabledChatInput(false);

            if ((window as any).mobileCheck && (window as any).mobileCheck()) {
                (document.activeElement as HTMLElement)?.blur();
            }
        }, 2000),
        [getChatUrl]
    );

    const sendReaction = useCallback(
        debounce(() => {
            const tempObj = {
                sender: {
                    userId: userId !== '' ? userId : 'notLoggedId',
                    name: username !== '' ? username : 'notLoggedName',
                    email: email !== '' ? email : 'notLoggedEmail',
                    admin: false,
                },
                message: {
                    emoji: {
                        unicode: '\u2764\uFE0F',
                        level: 1,
                    },
                },
            };

            const currentMedia = mediasData.find(({ id, originalId }: any) => id === mediaId || originalId === mediaId);
            window.parent.postMessage(
                {
                    origin: 'Qbrick.Interact.Widget.Element',
                    identifier: 'qbrick-player-analytics-message',
                    command: {
                        type: 'AnalyticsEvent',
                        action: 'click',
                        data: 'reaction',
                        mediaName: mediaName,
                        mediaId: mediaId,
                        accountId: accountId,
                        duration: currentMedia?.duration ?? duration,
                        isMediaLive: isMediaLive,
                        currentPosition: isMediaLive ? Date.now() : currentPosition,
                    },
                },
                '*'
            );

            fetch(reactionUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(tempObj),
            });
        }, 2000),
        [userId, username, email, mediasData, mediaId, mediaName, accountId, duration, isMediaLive, currentPosition, reactionUrl]
    );

    const getChatUserInfoFromCookie = useCallback(() => {
        const userIdCookie = Cookies.get(`chat_ID_${accountId}`);
        const userNameCookie = Cookies.get(`chat_name_${accountId}`);
        const userEmailCookie = Cookies.get(`chat_email_${accountId}`);

        if (isIgnoreChatCookies()) {
            Cookies.remove(`chat_ID_${accountId}`);
            Cookies.remove(`chat_name_${accountId}`);
            Cookies.remove(`chat_email_${accountId}`);
            Cookies.remove(`chat_upvoted_${accountId}`);
            setChatUserId('');
            setChatUsername('');
            setChatEmail('');
        } else {
            userIdCookie && setChatUserId(userIdCookie);
            userNameCookie && setChatUsername(userNameCookie);
            userEmailCookie && setChatEmail(userEmailCookie);
        }
    }, [accountId, isIgnoreChatCookies, setChatUserId, setChatUsername, setChatEmail]);

    const checkAndScrollToBottom = useCallback(() => {
        const currentMessageContainer = messageContainerRef.current;
        if (!currentMessageContainer) {
            return;
        }

        if (
            justSendMessage.current ||
            Math.abs(currentMessageContainer.scrollHeight - currentMessageContainer.scrollTop - currentMessageContainer.clientHeight) <= 100
        ) {
            scrollToBottom();
        }
    }, []);

    const scrollToBottom = useCallback(() => {
        if (!messageContainerRef.current) {
            return;
        }

        const allMessages = messageContainerRef.current.querySelectorAll('.chat-message');
        const lastMessage = allMessages[allMessages.length - 1];
        justSendMessage.current = false;
        if (lastMessage) {
            lastMessage.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, []);

    const enableBasketObserver = useCallback(() => {
        const target = container.querySelector('.gobrain-shopping-basket');
        if (!target) {
            return;
        }

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes') {
                    if (target.getAttribute('style') === 'display: block;') {
                        setChatVisible(false);
                        setShowBackgroundElements(false);
                    } else if (target.getAttribute('style') === 'display: none;') {
                        setChatVisible(true);
                        setShowBackgroundElements(true);
                    }
                }
            });
        });

        const config = {
            attributes: true,
        };

        observer.observe(target, config);
    }, [setShowBackgroundElements]);

    const enablePreBasketObserver = useCallback(() => {
        const target = container.querySelector('.gobrain-shopping-pre-basket');
        if (!target) {
            return;
        }

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes') {
                    if (target.getAttribute('style') === 'display: block;') {
                        setChatVisible(false);
                        setShowBackgroundElements(false);
                    } else if (target.getAttribute('style') === 'display: none;') {
                        setChatVisible(true);
                        setShowBackgroundElements(true);
                    }
                }
            });
        });

        const config = {
            attributes: true,
        };

        observer.observe(target, config);
    }, [setShowBackgroundElements]);

    const enableItemsCountersObserver = useCallback(() => {
        const target = container.querySelector('.gobrain-show-basket-item-count');
        if (!target) {
            return;
        }

        const observer = new MutationObserver(() => {
            setBasketItemCount(Number(container.querySelector('.gobrain-show-basket-item-count')?.firstChild?.textContent) || 0);
        });

        const config = {
            attributes: true,
            characterData: true,
            childList: true,
            subtree: true,
            attributeOldValue: true,
            characterDataOldValue: true,
        };

        observer.observe(target, config);
    }, []);

    const updatePollState = useCallback((polls: Poll[]) => {
        if (polls && polls.length > 0 && polls.find) {
            const lastPublishedPoll = polls.find((p) => p.published);
            if (lastPublishedPoll) {
                setPollArray(polls);
                setPublishedPoll(lastPublishedPoll);
                setPollResult(lastPublishedPoll.closed ? lastPublishedPoll.result : undefined);
            } else {
                setPollArray(polls);
                setPublishedPoll(undefined);
                setPollResult(undefined);
            }
        } else {
            setPollArray([]);
            setPublishedPoll(undefined);
            setPollResult(undefined);
        }
    }, []);

    const updateQnAState = useCallback((qnas: QnA[], questions: Question[]) => {
        const lastPublishedQna = qnas.find((qna) => qna.published);
        const approvedQs = (questions ?? []).filter((q) => q.approved && !q.discarded);
        setPublishedQnA(lastPublishedQna);
        setApprovedQuestions(approvedQs);
        setAllQuestions(questions);
    }, []);

    const fetchChatMessages = useCallback(() => {
        if (!isChatActivated) {
            return;
        }
        fetch(getChatUrl)
            .then((res) => res.json())
            .then((data) => {
                const tempChatMessages: Message[] = [];
                let tempPinnedMessagesArray: Message[] = [];

                data.forEach((item: Message) => {
                    if (item.message?.pinned) {
                        tempPinnedMessagesArray = [];
                        tempPinnedMessagesArray.push(item);
                    }

                    tempChatMessages.push(item);
                });

                setMessagesArray(tempChatMessages);
                setPinnedMessagesArray(tempPinnedMessagesArray);
            });
    }, [getChatUrl, isChatActivated]);

    const fetchPollData = useCallback(() => {
        const pollUrl = `${chatPublicDomain}${accountId}/medias/${mediaId}/polls`;
        fetch(
            pollUrl,
            getRestrictToken(mediaId)
                ? {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Authorization': 'AVP1 ' + getRestrictToken(mediaId),
                    },
                }
                : undefined
        )
            .then((res) => res.json())
            .then((data) => {
                setPollsData(data);
                updatePollState(data);
            });
    }, [accountId, mediaId, getRestrictToken, setPollsData, updatePollState]);

    const fetchQuestions = useCallback(
        (qnas: QnA[]) => {
            const questionUrl = `${chatPublicDomain}${accountId}/medias/${mediaId}/qnas/${qnas[0].id}/questions`;
            fetch(
                questionUrl,
                getRestrictToken(mediaId)
                    ? {
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Authorization': 'AVP1 ' + getRestrictToken(mediaId),
                        },
                    }
                    : undefined
            )
                .then((res) => res.json())
                .then((questions) => {
                    setQuestionsData(questions ?? []);
                    updateQnAState(qnas, questions);
                });
        },
        [accountId, mediaId, getRestrictToken, setQuestionsData, updateQnAState]
    );

    const fetchQnAData = useCallback(() => {
        const qnaUrl = `${chatPublicDomain}${accountId}/medias/${mediaId}/qnas`;
        fetch(
            qnaUrl,
            getRestrictToken(mediaId)
                ? {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Authorization': 'AVP1 ' + getRestrictToken(mediaId),
                    },
                }
                : undefined
        )
            .then((res) => res.json())
            .then((qnas) => {
                setQnAData(qnas ?? []);
                if (qnas?.[0]?.id) {
                    fetchQuestions(qnas);
                }
            });
    }, [accountId, mediaId, getRestrictToken, setQnAData, fetchQuestions]);

    const fetchChatData = useCallback(() => {
        setGetChatUrl(chatPublicDomain + accountId + '/medias/' + mediaId + '/messages');
        setReactionUrl(chatPublicDomain + accountId + '/medias/' + mediaId + '/reactions');
    }, [accountId, mediaId, setReactionUrl]);

    const parseLiveCustomMessage = useCallback(
        (response: MessageEvent) => {
            const responseMessage = JSON.parse(response.data);

            if (responseMessage?.key === NOTIFICATION_MESSAGES.customPropUpdated && responseMessage.body?.mediaId === mediaId) {
                const customProperty = JSON.parse(responseMessage.body?.customProperty?.value ?? '');
                const shouldActivateViewerEngagementModule = customProperty?.activatedViewerEngagement && !customProperty?.hideEngagementModule;
                const shouldActivateChat = customProperty?.activatedChat !== false;

                const currentMedia = mediasData.find((media: any) => media?.id === mediaId || media?.originalId === mediaId);
                if (currentMedia) {
                    currentMedia.custom = customProperty;
                    const newMediasData = [...mediasData].map((media: any) => (media.id === mediaId ? currentMedia : media));
                    setMediasData(newMediasData);
                }

                if (viewerEngagementModuleActivated !== shouldActivateViewerEngagementModule) {
                    setViewerEngagementModuleActivated(shouldActivateViewerEngagementModule);
                }
                if (shouldActivateChat !== isChatActivated) {
                    setChatActivated(shouldActivateChat);
                }
                if (!!customProperty?.closeIncomingMessage !== isCloseIncomingMessage) {
                    setCloseIncomingMessage(!!customProperty?.closeIncomingMessage);
                }

                if (customProperty?.livemanager?.liveTranscriptLanguage) {
				    const liveTranscriptLanguage = customProperty?.livemanager?.liveTranscriptLanguage || '';
				    liveTranscriptLanguage && setLiveTranscriptLanguage(liveTranscriptLanguage);
			    }

                if (customProperty?.livemanager?.enabledLiveTranscript !== undefined) {
                    const enabledLiveTranscript = customProperty?.livemanager?.enabledLiveTranscript;
                    setEnabledLiveTranscript(enabledLiveTranscript);
                }
            }

            if (responseMessage?.key === NOTIFICATION_MESSAGES.liveTranscriptMessage && responseMessage.body?.mediaId === mediaId && responseMessage.body?.tenantId) {
			    addTranscription(responseMessage.body);
		    };
        },
        [mediaId, mediasData, viewerEngagementModuleActivated, isChatActivated, isCloseIncomingMessage, setMediasData, setViewerEngagementModuleActivated, setChatActivated, setCloseIncomingMessage, addTranscription, setLiveTranscriptLanguage, setEnabledLiveTranscript]
    );

    const parseQnAMessages = useCallback((response: MessageEvent) => {
        const responseMessage = JSON.parse(response.data);
        const messageKey = responseMessage.key;
        const responseMessageBody = responseMessage.body ?? {};

        switch (messageKey) {
            case NOTIFICATION_MESSAGES.qnaQuestionCreated:
                setAllQuestions((prevState) => [...prevState, { ...responseMessageBody, created: responseMessageBody.timestamp }]);
                break;
            case NOTIFICATION_MESSAGES.qnaPublished:
                setSeenEngagementCounts(true);
                setPublishedQnA(responseMessageBody);
                break;
            case NOTIFICATION_MESSAGES.qnaUnpublished:
                setPublishedQnA((prevState) => {
                    if (!prevState || prevState.id !== responseMessageBody.id) {
                        return prevState;
                    }
                    return undefined;
                });
                break;
            case NOTIFICATION_MESSAGES.qnaUpdated:
                setPublishedQnA((prevState) => {
                    if (!prevState || prevState.id !== responseMessageBody.id) {
                        return prevState;
                    }
                    return responseMessageBody;
                });
                break;
            case NOTIFICATION_MESSAGES.qnaQuestionDiscardChanged:
                setAllQuestions((prevState) => {
                    const filteredAllQuestions = prevState.filter((q) => q.id !== responseMessageBody.id && q.recipient?.replyQuestionId !== responseMessageBody.id);
                    const updatedQuestion = prevState.find((q) => q.id === responseMessageBody.id);
                    const updatedReply = prevState.find((q) => q.recipient?.replyQuestionId === responseMessageBody.id);
                    if (!updatedQuestion) {
                        return prevState;
                    }
                    updatedQuestion.discarded = responseMessageBody.discarded;

                    // In case the question doesn't have replied yet.
                    if (updatedReply) {
                        updatedReply.discarded = responseMessageBody.discarded;
                    }
                    const newAllQuestions = [...filteredAllQuestions, updatedQuestion, updatedReply].filter(Boolean) as Question[];
                    const newApprovedQuestions = newAllQuestions.filter((q) => q.approved && !q.discarded);

                    setApprovedQuestions(newApprovedQuestions);
                    return newAllQuestions;
                });
                break;
            case NOTIFICATION_MESSAGES.qnaQuestionApproveChanged:
                setAllQuestions((prevState) => {
                    const filteredAllQuestions = prevState.filter((q) => q.id !== responseMessageBody.id);
                    const updatedQuestion = prevState.find((q) => q.id === responseMessageBody.id);
                    if (!updatedQuestion) {
                        return prevState;
                    }
                    updatedQuestion.approved = responseMessageBody.approved;
                    // update created value to make sure new question will be on bottom of messages
                    updatedQuestion.created = new Date().toISOString();

                    const newAllQuestions = [...filteredAllQuestions, updatedQuestion].filter(Boolean) as Question[];
                    const newApprovedQuestions = newAllQuestions.filter((q) => q.approved && !q.discarded);

                    setSeenEngagementCounts(true);
                    setApprovedQuestions(newApprovedQuestions);
                    return newAllQuestions;
                });
                break;
            case NOTIFICATION_MESSAGES.qnaQuestionAnswered:
                setAllQuestions((prevState) => {
                    const filteredAllQuestions = prevState.filter((q) => q.id !== responseMessageBody.id);
                    const originalQuestion = prevState.find((q) => q.id === responseMessageBody.id);

                    if (!originalQuestion) {
                        return prevState;
                    }

                    // BE now return 2 records for question and answer, therefore doing the same for ui
                    const answeredQuestion = {
                        ...originalQuestion,
                        recipient: responseMessageBody.recipient,
                        reply: responseMessageBody.reply,
                        sender: responseMessageBody.sender,
                        created: new Date().toISOString(),
                    };

                    const newAllQuestions = [...filteredAllQuestions, originalQuestion, answeredQuestion].filter(Boolean) as Question[];
                    const newApprovedQuestions = newAllQuestions.filter((q) => q.approved && !q.discarded);

                    setSeenEngagementCounts(true);
                    setApprovedQuestions(newApprovedQuestions);
                    return newAllQuestions;
                });
                break;
            case NOTIFICATION_MESSAGES.qnaQuestionUpvoted:
                setAllQuestions((prevState) => {
                    const filteredAllQuestions = prevState.filter((q) => q.id !== responseMessageBody.id);
                    const updatedReply = prevState.find((q) => q.id === responseMessageBody.id && q.reply);
                    const updatedQuestion = prevState.find((q) => q.id === responseMessageBody.id && !!q.question);
                    if (!updatedQuestion) {
                        return prevState;
                    }
                    updatedQuestion.votes = responseMessageBody.votes;

                    const newAllQuestions = [...filteredAllQuestions, updatedReply, updatedQuestion].filter(Boolean) as Question[];
                    const newApprovedQuestions = newAllQuestions.filter((q) => q.approved && !q.discarded);
                    setApprovedQuestions(newApprovedQuestions);
                    return newAllQuestions;
                });
                break;
        }
    }, []);

    const parseChatMessages = useCallback((response: MessageEvent) => {
        const responseMessage = JSON.parse(response.data);
        const messageKey = responseMessage.key;
        const responseMessageBody = responseMessage.body ?? {};

        switch (messageKey) {
            case NOTIFICATION_MESSAGES.messageCreated:
                setMessagesArray((prevState) => {
                    const filteredMessages = prevState.filter((m) => m.id !== responseMessageBody.id);
                    const newMessage = { ...responseMessageBody, created: responseMessageBody.created ?? responseMessageBody.timestamp };
                    setPinnedMessagesArray(responseMessageBody.message?.pinned ? [newMessage] : pinnedMessagesArray);
                    newMessage && setSeenEngagementCounts(true);
                    return [...filteredMessages, newMessage];
                });
                break;

            case NOTIFICATION_MESSAGES.messagePinned:
                setMessagesArray((prevState) => {
                    const newMessageArray = (prevState ?? []).map((messBody) => ({
                        ...messBody,
                        message: {
                            ...messBody.message,
                            pinned: messBody.id === responseMessageBody.id,
                        },
                    }));
                    const currentMess = newMessageArray.find((messBody) => messBody.id === responseMessageBody.id);

                    if (currentMess) {
                        setPinnedMessagesArray([currentMess]);
                    }

                    return newMessageArray;
                });
                break;

            case NOTIFICATION_MESSAGES.messageUnPinned:
                setMessagesArray((prevState) => {
                    const newMessageArray = (prevState ?? []).map((messBody) => ({
                        ...messBody,
                        message: {
                            ...messBody.message,
                            pinned: messBody.id === responseMessageBody.id ? false : messBody.message.pinned,
                        },
                    }));

                    setPinnedMessagesArray([]);

                    return newMessageArray;
                });
                break;

            case NOTIFICATION_MESSAGES.messageBlocked:
                setMessagesArray((prevState) => {
                    const newMessageArray = (prevState ?? []).map((messBody) => {
                        if (responseMessageBody.messageIds.includes(messBody.id)) {
                            return undefined;
                        }
                        return messBody;
                    }).filter(Boolean) as Message[];

                    const currentPinnedMess = pinnedMessagesArray.find((messBody) => responseMessageBody.messageIds.includes(messBody.id));
                    const newPinnedMessageArray = currentPinnedMess ? [] : pinnedMessagesArray;

                    setPinnedMessagesArray(newPinnedMessageArray);

                    return newMessageArray;
                });
                break;

            case NOTIFICATION_MESSAGES.reactionCreated:
                setReactionId(new Date().getUTCMilliseconds().toString());
                break;

            default:
                return;
        }
    }, [pinnedMessagesArray, setReactionId]);

    const parsePollMessages = useCallback(
        (response: MessageEvent) => {
            const responseMessage = JSON.parse(response.data);
            const messageKey = responseMessage.key;
            const responseMessageBody = responseMessage.body ?? {};

            switch (messageKey) {
                case NOTIFICATION_MESSAGES.pollCreated:
                    setPollArray((prevState) => [...prevState, responseMessageBody]);
                    break;

                case NOTIFICATION_MESSAGES.pollUpdated:
                    setPollArray((prevState) => {
                        const newPolls = prevState.map((prevPoll) => (prevPoll.id === responseMessageBody.id ? responseMessageBody : prevPoll));
                        if (!prevState.find((p) => p.id === responseMessageBody.id)) {
                            newPolls.push(responseMessageBody);
                        }

                        return newPolls;
                    });
                    break;

                case NOTIFICATION_MESSAGES.pollPublished:
                    setSeenEngagementCounts(true);
                    setPublishedPoll(responseMessageBody);
                    setPollResult(responseMessageBody.result);
                    setTimeout(() => setPollExpanded(true));
                    break;

                case NOTIFICATION_MESSAGES.pollUnpublished:
                    setPublishedPoll((prevState) => {
                        if (!prevState || prevState.id !== responseMessageBody.id) {
                            return prevState;
                        }
                        setPollResult(undefined);
                        setTimeout(() => setPollExpanded(false));
                        return undefined;
                    });
                    break;

                case NOTIFICATION_MESSAGES.pollClosed:
                    setPublishedPoll({ ...responseMessageBody, closed: true });
                    setPollResult(responseMessageBody.result);
                    responseMessageBody.result && setSeenEngagementCounts(true);
                    break;
                default:
                    return;
            }
        },
        [setPollExpanded]
    );

    const parseMessages = useCallback(
        (response: MessageEvent) => {
            parseLiveCustomMessage(response);
            if (!isCloseIncomingMessage) {
                parseChatMessages(response);
                parsePollMessages(response);
                parseQnAMessages(response);
            }
        },
        [parseLiveCustomMessage, isCloseIncomingMessage, parseChatMessages, parsePollMessages, parseQnAMessages]
    );

    const onSocketOpen = useCallback((webSocketConn: WebSocket) => {
        if (NotificationUtils.isSocketConnectionReady(webSocketConn)) {
            scrollToBottom();
        }
    }, [scrollToBottom]);

    const connectToTheSocket = useCallback(() => {
        if (webSocketConnection.current && webSocketConnection.current.close) {
            webSocketConnection.current.close();
        }

        const playerElement = container.querySelector(`#player-${playlistId || mediaId}-${configParams.containerKey}`);
        if (!playerElement) {
            return;
        }

        webSocketConnection.current = NotificationUtils.connectToSocketServer({
            onOpen: onSocketOpen,
            onMessage: parseMessages,
            mediaId,
            accountId,
        });
    }, [playlistId, mediaId, onSocketOpen, parseMessages, accountId, configParams.containerKey]);

    const handleFocusOnChatInput = useCallback(() => {
        if (username === '') {
            setShowLoginForm(true);
            container.querySelector('#chatInputFieldId')?.blur();
        }
    }, [username, setShowLoginForm]);

    const handleEditProfileClick = useCallback(() => {
        setShowLoginForm(username === '' ? true : 'edit');
    }, [username, setShowLoginForm]);

    const onUpvoteButtonClick = useCallback(
        (event: any, questionId: string, qnaId?: string) => {
            qnaId && upvoteQuestion(event, questionId, qnaId);
        },
        [upvoteQuestion]
    );

    const handleSendQuestion = useCallback(
        (event: any, question: { text?: string }) => {
            if (question?.text) {
                const questionBody = {
                    id: generateUUID(),
                    question: question.text,
                    sender: {
                        userId: userId,
                        name: username,
                        email: email,
                        admin: false,
                    },
                };
                sendQuestion(event, questionBody);
            }
        },
        [userId, username, email, sendQuestion]
    );

    const handleMessagePost = useCallback(
        (event: any, message: string, setMessageText?: (_text: string) => void) => {
            if (event.keyCode === 13 && message !== '') {
                const messageBody = {
                    id: generateUUID(),
                    sender: {
                        userId: userId,
                        name: username,
                        email: email,
                        admin: false,
                    },
                    message: {
                        pinned: false,
                        text: message,
                    },
                };

                setInputChatString('');
                setDisabledChatInput(true);
                setMessagesArray((prevState) => [...prevState, { ...messageBody, created: new Date().toISOString() }]);
                setMessageText && setMessageText('');

                sendMessage(event, messageBody);
            }
        },
        [userId, username, email, sendMessage]
    );

    const onEmojiClick = useCallback((_event: any, emojiObject: { emoji: string }) => {
        setInputChatString((prev) => prev + emojiObject.emoji);
        setShowPicker(false);
    }, []);

    const handleReactionPost = useCallback(() => {
        sendReaction();
    }, [sendReaction]);

    const handleRefreshButtonClick = useCallback(() => {
        fetchPollData();
        fetchQnAData();
        fetchChatMessages();
    }, [fetchPollData, fetchQnAData, fetchChatMessages]);

    // Effects
    useEffect(() => {
        connectToTheSocket();
        enableBasketObserver();
        enablePreBasketObserver();
        enableItemsCountersObserver();
        scrollToBottom();
        getChatUserInfoFromCookie();

        const shouldRefetchChatData = viewerEngagementModuleActivated && settings?.engagementFeatures?.enabled && settings?.engagementFeatures?.showChat;
        if (shouldRefetchChatData && isPlaying !== undefined) {
            fetchChatData();

            const bottomControlbar = container.getElementsByClassName('bottomControlbar')[0];
            if (bottomControlbar) {
                chatWrapperBottom.current = bottomControlbar.getBoundingClientRect().height;
            }
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (showChatWindow) {
            setTimeout(scrollToBottom, 1000);
        }
    }, [showChatWindow, scrollToBottom]);

    useEffect(() => {
        checkAndScrollToBottom();
    }, [messagesArray, checkAndScrollToBottom]);

    useEffect(() => {
        const shouldRefetchChatData = viewerEngagementModuleActivated && isPlaying !== undefined && settings?.engagementFeatures?.enabled && settings?.engagementFeatures?.showChat;

        if (shouldRefetchChatData) {
            connectToTheSocket();
            fetchChatData();
        }

        if (isPlaying !== undefined) {
            const bottomControlbar = container.getElementsByClassName('bottomControlbar')[0];
            chatWrapperBottom.current = bottomControlbar ? bottomControlbar.getBoundingClientRect().height : 60;
        }
    }, [mediaId, viewerEngagementModuleActivated, isChatActivated, isPlaying]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (playerReady) {
            connectToTheSocket();
            enableBasketObserver();
            enablePreBasketObserver();
            enableItemsCountersObserver();
        }
    }, [playerReady]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (getChatUrl) {
            fetchChatMessages();
            fetchPollData();
            fetchQnAData();
        }
    }, [getChatUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    // Render
    const isShowViewerEngagementModule = viewerEngagementModuleActivated && settings?.engagementFeatures?.enabled && settings?.engagementFeatures?.showChat;

    const currentMediaForPresenter = mediasData.find(({ id, originalId }: any) => id === mediaId || originalId === mediaId);
    const isInsidePresenter = PresenterHelper.hasPresentationLayout({ publishedSlides: presentationPublishedSlides, media: currentMediaForPresenter });

    if (showShoppingCart || isPlaying === undefined || !isShowViewerEngagementModule || showCountdownModule) {
        return null;
    }

    if (isUsedOnMobile(configParams) && !isInsidePresenter) {
        return (
            <>
                <ChatOverlayContainer
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    hideRightControls={false}
                    className={joinClassNames(['chat-overlay-mobile', playerHover || settings?.bottomControls?.alwaysVisible ? 'chat-overlay-mobile--hover' : ''])}
                >
                    <ChatOverlayWrapper
                        isMobile
                        chatVisible={chatVisible}
                        showMobileChat={showMobileChat}
                        style={playerHover || settings?.bottomControls?.alwaysVisible ? { bottom: chatWrapperBottom.current + 'px' } : undefined}
                    >
                        <ChatWrapper showMobileChat={showMobileChat} className="chat-wrapper">
                            {isShowViewerEngagementModule && (
                                <>
                                    {isChatActivated && (
                                        <ChatBottom
                                            isMobile
                                            onEditProfileClick={handleEditProfileClick}
                                            inputChatString={inputChatString}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const value = e.target.value;
                                                if (value.trim().length > 300) {
                                                    return;
                                                }
                                                setInputChatString(value);
                                            }}
                                            handleMessagePost={handleMessagePost}
                                            handleFocusOnChatInput={handleFocusOnChatInput}
                                            emojiIconOnClick={() => setShowPicker((prev) => !prev)}
                                            emojiOnClick={(e: any, emojiObject: { emoji: string }) => onEmojiClick(e, emojiObject)}
                                            showPicker={showPicker}
                                            disabledChatInput={disabledChatInput || isCloseIncomingMessage}
                                        />
                                    )}
                                    {isChatActivated && <ChatPinnedMessage settings={settings} pinnedMessagesArray={pinnedMessagesArray as any[]} messagesArray={messagesArray as any[]} />}
                                    {!isCloseIncomingMessage && <Poll settings={settings} poll={publishedPoll} pollResult={pollResult} isMobile />}
                                    {!isCloseIncomingMessage && (
                                        <QuestionPanel
                                            t={t}
                                            qna={publishedQnA}
                                            onSendQuestionClick={handleSendQuestion}
                                            onFocusOnQuestionField={handleFocusOnChatInput}
                                            settings={settings}
                                            isMobile
                                        />
                                    )}
                                    <ChatTop
                                        settings={settings}
                                        isPollExpanded={isPollExpanded}
                                        messageContainerRef={messageContainerRef}
                                        messages={isChatActivated ? messagesArray : [] as any[]}
                                        questions={approvedQuestions as any[]}
                                        onRefreshButtonClick={handleRefreshButtonClick}
                                        onUpvoteButtonClick={onUpvoteButtonClick}
                                        closeIncomingMessage={isCloseIncomingMessage}
                                        qna={isCloseIncomingMessage ? undefined : publishedQnA}
                                    />
                                </>
                            )}
                        </ChatWrapper>
                        <ChatButtonsWrapper
                            isShowViewerEngagementModule={isShowViewerEngagementModule}
                            reactionId={reactionId}
                            settings={settings}
                            basketItemCount={basketItemCount}
                            handleReactionPost={handleReactionPost}
                        />
                    </ChatOverlayWrapper>
                </ChatOverlayContainer>
                {isShowViewerEngagementModule && <ChatLoginOverlay settings={settings} isMobile />}
            </>
        );
    }

    return showChatWindow ? (
        <ChatUpdated
            onChatNotificationClick={scrollToBottom}
            settings={settings}
            poll={isCloseIncomingMessage ? undefined : publishedPoll}
            qna={isCloseIncomingMessage ? undefined : publishedQnA}
            pollResult={isCloseIncomingMessage ? undefined : pollResult}
            messages={messagesArray as any[]}
            questions={approvedQuestions as any[]}
            showChatWindow={showChatWindow}
            onKeyUp={handleMessagePost}
            onSendQuestionClick={handleSendQuestion}
            onFocusOnQuestionField={handleFocusOnChatInput}
            messageContainerRef={messageContainerRef}
            onChatInputFocus={handleFocusOnChatInput}
            onEditProfileClick={handleEditProfileClick}
            onClose={() => setShowChatWindow(false)}
            disabledChatInput={disabledChatInput}
            onRefreshButtonClick={handleRefreshButtonClick}
            onUpvoteButtonClick={onUpvoteButtonClick}
            closeIncomingMessage={isCloseIncomingMessage}
            isMobile={isUsedOnMobile(configParams)}
        />
    ) : null;
};

export default ChatOverlay;
